import React, { useState } from 'react';
import { Calendar, CalendarRange, Clock, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';

export interface DateFilterOptions {
  type: 'all' | 'today' | 'yesterday' | 'week' | 'month' | 'quarter' | 'year' | 'custom';
  startDate?: string;
  endDate?: string;
  quarter?: number;
  year?: number;
  month?: number;
}

interface AdvancedDateFilterProps {
  value: DateFilterOptions;
  onChange: (filter: DateFilterOptions) => void;
  className?: string;
}

export function AdvancedDateFilter({ value, onChange, className }: AdvancedDateFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempFilter, setTempFilter] = useState<DateFilterOptions>(value);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);
  const quarters = [
    { value: 1, label: 'Q1 (Jan-Mar)' },
    { value: 2, label: 'Q2 (Apr-Jun)' },
    { value: 3, label: 'Q3 (Jul-Sep)' },
    { value: 4, label: 'Q4 (Oct-Dec)' }
  ];
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const getFilterLabel = (filter: DateFilterOptions) => {
    switch (filter.type) {
      case 'all': return 'All Time';
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'quarter': 
        if (filter.quarter && filter.year) {
          const q = quarters.find(q => q.value === filter.quarter);
          return `${q?.label} ${filter.year}`;
        }
        return 'This Quarter';
      case 'year':
        return filter.year ? `Year ${filter.year}` : 'This Year';
      case 'custom':
        if (filter.startDate && filter.endDate) {
          return `${filter.startDate} to ${filter.endDate}`;
        }
        return 'Custom Range';
      default:
        return 'Filter';
    }
  };

  const applyFilter = () => {
    onChange(tempFilter);
    setIsOpen(false);
  };

  const resetFilter = () => {
    const resetFilter = { type: 'all' as const };
    setTempFilter(resetFilter);
    onChange(resetFilter);
    setIsOpen(false);
  };

  const quickFilters = [
    { type: 'all' as const, label: 'All Time' },
    { type: 'today' as const, label: 'Today' },
    { type: 'yesterday' as const, label: 'Yesterday' },
    { type: 'week' as const, label: 'This Week' },
    { type: 'month' as const, label: 'This Month' },
    { type: 'quarter' as const, label: 'This Quarter' },
    { type: 'year' as const, label: 'This Year' }
  ];

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`justify-start ${className}`}>
          <Calendar className="mr-2 h-4 w-4" />
          {getFilterLabel(value)}
          {value.type !== 'all' && (
            <Badge variant="secondary" className="ml-2 h-5">
              <Filter className="h-3 w-3" />
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <Card className="border-0 shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <CalendarRange className="h-5 w-5" />
                Date Filter
              </span>
              {value.type !== 'all' && (
                <Button variant="ghost" size="sm" onClick={resetFilter}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quick Filters */}
            <div>
              <Label className="text-sm font-medium">Quick Filters</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {quickFilters.map((filter) => (
                  <Button
                    key={filter.type}
                    variant={tempFilter.type === filter.type ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTempFilter({ type: filter.type })}
                    className="justify-start"
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
            </div>

            <Separator />

            {/* Specific Period Filters */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Specific Periods</Label>
              
              {/* Quarter Filter */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Quarter</Label>
                  <Select 
                    value={tempFilter.quarter?.toString()} 
                    onValueChange={(value) => setTempFilter({
                      ...tempFilter,
                      type: 'quarter',
                      quarter: parseInt(value),
                      year: tempFilter.year || currentYear
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Q" />
                    </SelectTrigger>
                    <SelectContent>
                      {quarters.map((q) => (
                        <SelectItem key={q.value} value={q.value.toString()}>
                          {q.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Year</Label>
                  <Select 
                    value={tempFilter.year?.toString()} 
                    onValueChange={(value) => setTempFilter({
                      ...tempFilter,
                      type: tempFilter.quarter ? 'quarter' : 'year',
                      year: parseInt(value)
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Month Filter */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Month</Label>
                  <Select 
                    value={tempFilter.month?.toString()} 
                    onValueChange={(value) => setTempFilter({
                      ...tempFilter,
                      type: 'month',
                      month: parseInt(value),
                      year: tempFilter.year || currentYear
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Month" />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month) => (
                        <SelectItem key={month.value} value={month.value.toString()}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Year</Label>
                  <Select 
                    value={tempFilter.year?.toString()} 
                    onValueChange={(value) => setTempFilter({
                      ...tempFilter,
                      year: parseInt(value)
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Custom Date Range */}
            <div>
              <Label className="text-sm font-medium">Custom Date Range</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className="text-xs">Start Date</Label>
                  <Input
                    type="date"
                    value={tempFilter.startDate || ''}
                    onChange={(e) => setTempFilter({
                      ...tempFilter,
                      type: 'custom',
                      startDate: e.target.value
                    })}
                  />
                </div>
                <div>
                  <Label className="text-xs">End Date</Label>
                  <Input
                    type="date"
                    value={tempFilter.endDate || ''}
                    onChange={(e) => setTempFilter({
                      ...tempFilter,
                      type: 'custom',
                      endDate: e.target.value
                    })}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button onClick={applyFilter} className="flex-1">
                Apply Filter
              </Button>
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </PopoverContent>
    </Popover>
  );
}